import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { syncManager } from '../../services/sync';
import { useAuth } from '../../hooks/useAuth';
import { feedbackBus } from '../CJMSView/services/feedbackBus';

interface Referral {
  id: string;
  name: string;
  email: string;
  phone?: string;
  businessName?: string;
  status: 'pending' | 'accepted';
  link: string;
}

export const PublicReferralForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientIndustry: '',
    referralRelationship: 'Friend' | 'Family' | 'Colleague' | 'Other',
    notes: '',
    referredBy: user?.email || '',
  });

   const [errors, setErrors] = useState<Record<string, string>>({});
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [status, setStatus] = useState<'idle' | 'success' | 'error' | 'auth_required'>('idle');
   const [successMessage, setSuccessMessage] = useState('');
   const [authError, setAuthError] = useState<string>('');

  // 5-referral progress tracker state
  const [referrals, setReferrals] = useState<Referral[]>([
    { id: '1', name: '', email: '', phone: '', businessName: '', status: 'pending', link: '' },
    { id: '2', name: '', email: '', phone: '', businessName: '', status: 'pending', link: '' },
    { id: '3', name: '', email: '', phone: '', businessName: '', status: 'pending', link: '' },
    { id: '4', name: '', email: '', phone: '', businessName: '', status: 'pending', link: '' },
    { id: '5', name: '', email: '', phone: '', businessName: '', status: 'pending', link: '' },
  ]);
  const [totalAccepted, setTotalAccepted] = useState(0);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    // Check if user has a referredBy email (from query params or session)
    const urlParams = new URLSearchParams(window.location.search);
    const referredBy = urlParams.get('referredBy');
    if (referredBy) {
      setFormData(prev => ({ ...prev, referredBy }));
    }
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientName.trim()) {
      newErrors.clientName = 'Client name is required';
    }

    if (!formData.clientEmail.trim()) {
      newErrors.clientEmail = 'Client email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.clientEmail)) {
      newErrors.clientEmail = 'Invalid email format';
    }

    if (!formData.clientPhone.trim()) {
      newErrors.clientPhone = 'Client phone is required';
    }

    if (!formData.clientIndustry.trim()) {
      newErrors.clientIndustry = 'Client industry is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleReferralChange = (id: string, field: keyof Referral, value: string) => {
    setReferrals(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  const checkReferralStatus = () => {
    const accepted = referrals.filter(r => r.status === 'accepted').length;
    setTotalAccepted(accepted);
    setUnlocked(accepted >= 5);
  };

  const shareAllLinks = () => {
    // Simulate sharing all referral links
    console.log('Sharing all referral links:', referrals);
  };

  const percentage = Math.round((totalAccepted / 5) * 100);

   const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // AUTHENTICATION CHECK: User must be logged in to submit referral
    if (!user?.email) {
      setStatus('auth_required');
      setSuccessMessage('🔒 Authentication required. Please log in first before submitting a referral.');
      // Redirect to login page after 2 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');

    try {
      const referralId = `REF-${Date.now().toString(36).toUpperCase()}${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

      // Submit to CRM_Leads tab (PUBLIC MODE)
      // NOTE: Referred_By_Email is stored in column M for linking referrals to referrer
      await syncManager.updateTabData('CRM_Leads', {
        Project_ID: referralId,
        Business_Name: formData.clientName,
        Email: formData.clientEmail,
        Phone: formData.clientPhone,
        Source: 'Public Referral Form',
        Status: 'new_lead',
        Created_Date: new Date().toISOString(),
        Last_Updated: new Date().toISOString(),
        Notes: `Referral from ${user.email} - ${formData.referralRelationship}`,
        // REFFERAL TRACKING COLUMNS (J-R)
        Referral_Status: 'pending',
        Referral_Date: new Date().toISOString(),
        Agreement_Status: 'not_agreed',
        Agreement_Date: null,
        Onboarding_Status: 'not_started',
        Onboarding_Date: null,
        Onboarding_Complete_Date: null,
        Partner_Status: 'none',
        Partner_Type: 'Level 1',
        // COMMISSION TRACKING COLUMNS (S-U)
        Commission_Status: 'pending',
        Commission_Amount: 0,
        Commission_Payout_Date: null,
        // CRITICAL: Links referral to referrer for tracking progress
        Referred_By_Email: user.email,
      });

      setStatus('success');
      setSuccessMessage(`Referral submitted successfully! ${formData.clientName} will be notified when they accept. They can track their progress in the Partner Portal.`);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          clientName: '',
          clientEmail: '',
          clientPhone: '',
          clientIndustry: '',
          referralRelationship: 'Friend',
          notes: '',
          referredBy: user.email || '',
        });
        setStatus('idle');
      }, 3000);

    } catch (e: any) {
      setStatus('error');
      setSuccessMessage(e.message || 'Failed to submit referral');
      feedbackBus.emit('activity_completed', {
        activityLabel: 'Referral submission failed',
        clientEmail: formData.clientEmail,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">🎁 Refer 5 Friends - FREE 1-Pager Website</h1>
          <p className="text-slate-600">
            Your friends get a free website, you get lifetime hosting access
          </p>
        </div>

        {/* Authentication Status Warning */}
        {status === 'auth_required' && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6 animate-in fade-in slide-in-from-top-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">🔒</div>
              <div>
                <p className="text-yellow-800 font-semibold mb-1">Authentication Required</p>
                <p className="text-yellow-700 text-sm mb-2">
                  You must be logged in to submit a referral. This ensures referrals are tracked to your account.
                </p>
                <p className="text-yellow-600 text-xs">
                  Redirecting to login page...
                </p>
              </div>
            </div>
          </div>
        )}

        {user?.email && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 text-sm">
              ✅ <strong>You are authenticated as:</strong> {user.email}
            </p>
            <p className="text-green-700 text-xs mt-1">
              Your referrals will be tracked to this account. You need 5 accepted referrals to unlock your FREE website.
            </p>
          </div>
        )}

        <div className="bg-slate-100 border-l-4 border-slate-600 p-4 rounded-lg mb-6">
          <p className="text-slate-800">
            How This Works: You share your personalized referral link with friends. When they accept the offer and complete the questionnaire, they get their FREE 1-pager website, and you get one step closer to unlocking your own FREE website!
          </p>
          <p className="text-slate-800 mt-2">
            <strong>Your goal:</strong> Get 5 friends to accept the offer → You unlock your FREE website questionnaire → AI generates your custom design → You get your FREE website in 10-15 minutes!
          </p>
        </div>

        {/* Referral Progress Tracker */}
        <div className="bg-slate-50 border-l-4 border-slate-600 p-4 rounded-lg mb-6">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Your Referral Progress</h3>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 bg-slate-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-indigo-600 h-full transition-all duration-500"
                style={{ width: `${percentage}%` }}
              >
                <div className="h-full flex items-center justify-center text-xs font-semibold text-white">
                  {percentage}%
                </div>
              </div>
            </div>
            <span className="text-sm font-medium text-slate-700">{totalAccepted}/5 accepted</span>
          </div>
          <p className="text-xs text-slate-600 mb-4">
            You need <strong>5 accepted referrals</strong> to unlock your FREE 1-pager website questionnaire.
          </p>

          <h4 className="text-sm font-semibold text-slate-800 mb-3">Your Referrals:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {referrals.map((referral) => (
              <div
                key={referral.id}
                className={`bg-white border rounded-lg p-3 transition-all ${
                  referral.status === 'accepted'
                    ? 'border-green-500 bg-green-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800 text-sm mb-1">
                      {referral.id}. {referral.name || 'Friend ' + referral.id}
                    </div>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded ${
                        referral.status === 'accepted'
                          ? 'bg-green-200 text-green-800'
                          : 'bg-yellow-200 text-yellow-800'
                      }`}
                    >
                      {referral.status === 'accepted' ? 'Accepted ✓' : 'Pending'}
                    </span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  <strong>Email:</strong> {referral.email}
                </div>
                {referral.phone && (
                  <div className="mt-1 text-xs text-slate-500">
                    <strong>Phone:</strong> {referral.phone}
                  </div>
                )}
                <div className="mt-2 text-xs text-slate-500">
                  <strong>Business:</strong> {referral.businessName || 'Not specified'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Unlock Message */}
        {unlocked && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6 animate-in fade-in slide-in-from-top-4">
            <p className="text-green-800 font-semibold text-sm">
              🎉 You've reached 5 accepted referrals! You can now complete your questionnaire.
            </p>
            <p className="text-green-700 mt-1 text-sm">
              Estimated time to launch: 10-15 minutes
            </p>
            <div className="mt-4 bg-white p-4 rounded-lg">
              <p className="text-sm text-slate-700">
                <strong>Next Steps:</strong>
              </p>
              <ol className="text-sm text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                <li>Log in to the Partner Portal</li>
                <li>Complete your questionnaire</li>
                <li>AI generates your custom website</li>
                <li>Your FREE website goes live!</li>
              </ol>
              <div className="mt-4 bg-slate-100 p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Support:</strong>
                </p>
                <p className="text-xs text-slate-700 mt-1">
                  Email: support@chrysalinks.co.za
                </p>
                <p className="text-xs text-slate-700">
                  WhatsApp: +27 81 714 2228
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Public Referral Form */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800 mb-6">Add New Referral</h3>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.clientName}
                  onChange={(e) => handleInputChange('clientName', e.target.value)}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 outline-none ${
                    errors.clientName ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="Full name"
                />
                {errors.clientName && <p className="text-xs text-red-500 mt-1">{errors.clientName}</p>}
              </div>

              {/* Client Email */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Client Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.clientEmail}
                  onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 outline-none ${
                    errors.clientEmail ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="client@example.com"
                />
                {errors.clientEmail && <p className="text-xs text-red-500 mt-1">{errors.clientEmail}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Client Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Client Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.clientPhone}
                  onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 outline-none ${
                    errors.clientPhone ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="+27 XX XXX XXXX"
                />
                {errors.clientPhone && <p className="text-xs text-red-500 mt-1">{errors.clientPhone}</p>}
              </div>

              {/* Client Industry */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Client Industry <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.clientIndustry}
                  onChange={(e) => handleInputChange('clientIndustry', e.target.value)}
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 outline-none ${
                    errors.clientIndustry ? 'border-red-500' : 'border-slate-300'
                  }`}
                  placeholder="Industry, e.g., Tech, Finance"
                />
                {errors.clientIndustry && <p className="text-xs text-red-500 mt-1">{errors.clientIndustry}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Referral Relationship */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Relationship
                </label>
                <select
                  value={formData.referralRelationship}
                  onChange={(e) => handleInputChange('referralRelationship', e.target.value as any)}
                  className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="Friend">Friend</option>
                  <option value="Family">Family</option>
                  <option value="Colleague">Colleague</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Business Name (Optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Business Name (Optional)
                </label>
                <input
                  type="text"
                  value={formData.clientIndustry} // Reusing industry field for business name in this demo
                  onChange={(e) => handleInputChange('clientIndustry', e.target.value)}
                  className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="Business name (for referral notes)"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={3}
                className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
                placeholder="Additional details about the referral..."
              />
            </div>

            {/* Status Messages */}
            {status === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium">{successMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isSubmitting || !user?.email}
                className={`flex-1 text-white font-semibold py-2 px-4 rounded-md transition-colors ${
                  !user?.email
                    ? 'bg-slate-400 cursor-not-allowed'
                    : isSubmitting
                    ? 'bg-indigo-400 cursor-wait'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Referral to CRM'}
              </button>
            </div>
          </form>
        </div>

        {/* Info Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg border border-slate-200">
            <h3 className="font-semibold text-slate-800 mb-2">📋 Key Features</h3>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>✓ Real-time form validation</li>
              <li>✓ Google Sheets CRM integration</li>
              <li>✓ Referral progress tracking (5 referrals)</li>
              <li>✓ Automatic status updates</li>
              <li>✓ Support contact provided</li>
            </ul>
          </div>

          <div className="bg-white p-4 rounded-lg border border-slate-200">
            <h3 className="font-semibold text-slate-800 mb-2">ℹ️ Information</h3>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>Referrals logged to CRM_Leads tab</li>
              <li>Prospects notified via email</li>
              <li>Progress tracked in Partner Portal</li>
              <li>Questions? Contact support</li>
              <li>Email: support@chrysalinks.co.za</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
